import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import {
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Select,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { draftCartActions } from '../../redux/features/draftCart/draftCart.slice';
import { DRAFT_CART_SCOPE } from '../../redux/features/draftCart/cartScope';
import { dialogActions } from '../../reducers/dialogSlice';
import { CartItem } from '../../utils/cart';
import Colors from '../../utils/color';
import { CartExpandCollapseState } from '../../constants/enums';
import CartItemModGroups from './CartItemModGroups';
import { useIsCartItemSelected } from './CartScopeContext';
import { selectSelectedItemCartId } from '../../selectors/dialog';
import TruncationTooltip from '../ItemBuilder/components/TruncationTooltip';

const MAX_QUANTITY = 19;
const QUANTITIES = Array.from({ length: MAX_QUANTITY }, (_, i) => i + 1);

const useStyles = makeStyles<Theme>(() => ({
  card: {
    marginBottom: 4,
    border: `1px solid ${Colors.alto}`,
    backgroundColor: Colors.white,
    // HITL-1617: the whole line opens in the centre pane now, so the whole line
    // reads as clickable rather than just its title.
    cursor: 'pointer',
  },
  /**
   * HITL-1619 — `!important`, and that is the whole of the fix.
   *
   * MUI's own `.MuiCardContent-root` ships `padding: 16px` with
   * `&:last-child { padding-bottom: 24px }`, and it is emotion-injected: a
   * plain `@mui/styles` declaration at equal specificity does not beat it.
   * This row asked for 6/8 and rendered at 16/24 — 8px of extra gutter on every
   * side and 18px of dead space under the stepper — beside an AI row that
   * forces its own with `!important` (`CartItemReview.contentHitl3`). The rule
   * was written; it simply never applied.
   *
   * Matched to that row rather than to the old 6/8: the two carts are one
   * component seen twice, and a line in My Cart should be the same height as
   * the AI line it is shadowing.
   */
  content: {
    padding: '8px !important',
    paddingBottom: '6px !important',
    // The same reset the AI row carries. `CartItemModGroups` indents itself by
    // 14px, and with the two columns side by side an indent on one and not on
    // the other reads as two different components rather than two carts.
    '& > div.cartitem-mod-group': {
      padding: '0 !important',
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
  },
  name: {
    flex: 1,
    minWidth: 0,
    // Matched to the AI row's `mainItemname`. `subtitle2` alone is MUI's 14px,
    // which put the same item name 13% larger in one column than the other.
    fontSize: '12px !important',
    lineHeight: '1.57 !important',
    fontWeight: 'bold !important',
    color: Colors.daintree,
    // HITL-1687: same rule as the AI row's `mainItemname`.
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  /**
   * HITL-1619 — the AI row's action bar, in My Cart.
   *
   * `space-between` with the stepper on the LEFT and Duplicate/Remove on the
   * right, matching `CartItemReview.actionsWrapper` + `actionsWrapperHitl3`.
   * This row shipped right-aligned and without Duplicate, which put the two
   * columns' controls in different places and gave the agent's own cart fewer
   * of them than the cart they cannot edit.
   */
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  // `stepperHitl3`, rule for rule: one segmented control reading − | n | +.
  stepper: {
    display: 'inline-flex',
    alignItems: 'stretch',
    border: `1px solid ${Colors.alto}`,
    borderRadius: 6,
    overflow: 'hidden',
    // Two classes deep, so this outranks MUI's own `.MuiIconButton-root`
    // without `!important` — exactly as the AI row does it.
    '& .MuiIconButton-root': {
      borderRadius: 0,
      padding: '2px 4px',
      color: Colors.daintree,
    },
    '& .MuiIconButton-root.Mui-disabled': {
      color: Colors.silverChalice,
    },
  },
  // `selectQuantity` + `selectQuantityHitl3`: the count is a jump-to menu, not a
  // label, so reaching 12 is one press in either column rather than eleven here
  // and one there. The arrow and the Input underline are hidden, which is what
  // makes it read as the middle cell of the stepper.
  quantity: {
    borderLeft: `1px solid ${Colors.alto}`,
    borderRight: `1px solid ${Colors.alto}`,
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
    '&:before, &:after': {
      borderBottom: 'none !important',
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '34px !important',
      padding: '0 4px !important',
      // No `fontWeight`, matching the AI row: `selectQuantityHitl3` sets none,
      // so the count sits at the input's normal weight. The 600 here was
      // carried over from the plain `<span>` this cell replaced, which needed
      // its own weight because it was not an input.
      color: Colors.daintree,
    },
  },
  // `row-reverse` with Remove first in the DOM, so the pair renders
  // Duplicate | Remove — the AI row's order, reached the same way.
  itemActions: {
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  iconAction: {
    // `!important` for the same reason as `content` above: MUI's `sizeSmall`
    // IconButton ships `padding: 5px` and wins over a plain JSS rule.
    padding: '4px !important',
    color: `${Colors.daintree} !important`,
  },
  iconActionDanger: {
    padding: '4px !important',
    color: `${Colors.guardsmanRed} !important`,
  },
}));

interface DraftCartItemReviewProps {
  cartItem: CartItem;
  quantity: number;
  collapseState: number;
}

/**
 * HITL-1614 — one line of My Cart.
 *
 * Deliberately NOT `CartItemReview`. That component is 669 lines welded to
 * `cartSlice`: every mutation routes through `useAgentInterception`, which
 * refuses and `logger.warn`s outside `Intercept`, and it reads
 * `selectIsAgentActionBlocked` to disable its own stepper, Remove and Duplicate.
 * Reused verbatim here it would grey out My Cart's controls in `Shadow` and log
 * a warning per press — the exact opposite of this story, whose whole point is a
 * cart the agent CAN edit while the AI drives.
 *
 * So the row is its own component writing `draftCart` directly. No interception,
 * no mode gate, no `sendOrder`: nothing in this column can reach the customer or
 * the POS, which is what makes it safe to leave live in a silent mode.
 *
 * The modifier tree is genuinely shared — `CartItemModGroups` and
 * `CartItemChildModGroup` read `dialogSlice` and their own props, never
 * `state.cart`, so they render a draft item unchanged.
 *
 * HITL-1617 makes the row SELECTABLE. HITL-1614 held it back with
 * `selectable={false}` because selecting drives the centre pane and the pane's
 * write path was hard-wired to `cartSlice` — a draft row claiming it would have
 * meant editing a draft item mutates the order. With the routing in place, the
 * pane follows the row into `draftCart`, which is the whole of "building into My
 * Cart".
 */
const DraftCartItemReview = ({
  cartItem,
  quantity,
  collapseState,
}: DraftCartItemReviewProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  // A draft is never sent, so it has no order status — and `shouldCollapse`
  // requires one. Collapse here is purely the agent's own toggle.
  const collapsed =
    collapseState === CartExpandCollapseState.AUTO_COLLAPSE ||
    collapseState === CartExpandCollapseState.MANUAL_COLLAPSE;

  const cartItemId = cartItem.cartItemId;

  // HITL-1715: the line the OmniBar is editing. My Cart never drew a selected
  // state, so an agent typing a modifier could not see which cart, let alone
  // which line, it was about to land on. Same 3px border the Live Order draws,
  // and scope-aware for the same reason: both carts number from 1.
  const selectedItemCartId = useAppSelector(selectSelectedItemCartId);
  const isSelected = useIsCartItemSelected(selectedItemCartId, cartItemId);

  const toggleCollapse = () => {
    dispatch(draftCartActions.setCartItemCollapseState({ cartItemId }));
  };

  // HITL-1617: open this line in the centre pane. `cartScope` is the whole
  // point — `cartItemId` alone is ambiguous across the two carts, and without it
  // `dialogSlice` would record a draft id against the main cart's id space.
  const openInCentrePane = () => {
    dispatch(
      dialogActions.selectItemAndResetNavigation({
        item: cartItem,
        itemCartId: cartItemId,
        cartScope: DRAFT_CART_SCOPE,
      })
    );
  };

  // Mirrors `CartItemReview.onCardClick`: a plain click on a cart line both
  // opens it in the centre pane and toggles its collapse, in either column. The
  // stepper and Remove stop propagation, so they never reach this handler.
  const onCardClick = () => {
    openInCentrePane();
    toggleCollapse();
  };

  const updateQuantity = (next: number) => {
    dispatch(
      draftCartActions.updateQuantity({
        cartItemId: cartItemId.toString(),
        quantity: next,
      })
    );
  };

  const removeItem = () => {
    dispatch(draftCartActions.deleteCartItem(cartItemId.toString()));
  };

  // HITL-1619: the AI row's Duplicate, on the draft slice. `cloneCartItem`
  // carries the quantity across, which `addItemToCart` would not.
  const duplicateItem = () => {
    dispatch(
      draftCartActions.cloneCartItem({ id: cartItemId.toString(), quantity })
    );
  };

  return (
    <Card
      className={classes.card}
      elevation={0}
      data-testid="draft-cart-item"
      data-selected={isSelected}
      onClick={onCardClick}
      // Inline rather than a new JSS rule: an extra rule renumbers class names
      // in unrelated snapshot suites.
      sx={isSelected ? { border: `3px solid ${Colors.daintree}` } : undefined}
    >
      <CardContent className={classes.content}>
        <div className={classes.titleRow} data-testid="draft-cart-item-title">
          <TruncationTooltip title={cartItem.name}>
            <Typography variant="subtitle2" className={classes.name}>
              {cartItem.name}
            </Typography>
          </TruncationTooltip>
          {collapsed ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ExpandLessIcon fontSize="small" />
          )}
        </div>

        {!collapsed && cartItem.childModifierGroups && (
          <CartItemModGroups
            cartItem={cartItem}
            modGroupCartItem={cartItem}
            quantity={quantity}
            selectable
          />
        )}

        {/*
          The stepper and Remove act on the line rather than opening it, so they
          swallow the click before the card's own handler sees it — same reason
          `CartItemReview` keeps a ref list of actionable elements.
        */}
        <div
          className={classes.actions}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={classes.stepper}>
            <IconButton
              aria-label="Decrease quantity"
              size="small"
              disabled={quantity <= 1}
              onClick={() => updateQuantity(quantity - 1)}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Select
              value={quantity}
              size="small"
              variant="standard"
              className={classes.quantity}
              inputProps={{ 'aria-label': 'Quantity' }}
              MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
              onChange={(event) => updateQuantity(event.target.value as number)}
            >
              {QUANTITIES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
            <IconButton
              aria-label="Increase quantity"
              size="small"
              disabled={quantity >= MAX_QUANTITY}
              onClick={() => updateQuantity(quantity + 1)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </div>
          {/* Remove first in the DOM; `row-reverse` renders Duplicate | Remove. */}
          <div className={classes.itemActions}>
            <IconButton
              className={classes.iconActionDanger}
              title="Remove"
              aria-label="Remove from My Cart"
              size="small"
              onClick={removeItem}
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              className={classes.iconAction}
              title="Duplicate"
              aria-label="Duplicate in My Cart"
              size="small"
              onClick={duplicateItem}
            >
              <ContentCopyOutlinedIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DraftCartItemReview;
